import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnInit, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzBreadCrumbModule } from 'ng-zorro-antd/breadcrumb';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzUploadModule } from 'ng-zorro-antd/upload';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { PersonalBroadcastListComponent } from 'src/app/routes/tenant/personal-broadcast/list/personal-broadcast-list.component';
import { PersonalInboundReasonComponent } from 'src/app/routes/tenant/personal-inbound-reason/list/personal-inbound-reason.component';
import { PersonalRobotSettingComponent } from 'src/app/routes/tenant/personal-robot-setting/personal-robot-setting.component';
import { PersonalWebchatSettingComponent } from 'src/app/routes/tenant/personal-webchat-setting/personal-webchat-setting.component';
import { ImageService } from 'src/app/services/image.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import {
  resolveCapabilityAppCodesFromApiCodes,
  TENANT_CAPABILITY_MANAGE_SERVICE_NUMBER_AUTHORIZATION
} from 'src/app/shared/utils/tenant-capability.util';
import { ConstantUtil } from 'src/app/util/constant.util';

import { ServiceNumberAuthComponent } from './auth/servicenumber-auth.component';
import { ServiceChannelComponent } from './channel/servicenumber-channel.component';
import { ChatExportComponent } from './chat-export/chat-export.component';
import { ServiceConfigComponent } from './config/servicenumber-config.component';
import { ServiceMemberComponent } from './member/servicenumber-member.component';
import { AvatarUploadModalComponent } from '../../avatar-modal/avatar-upload-modal.component';
import { IdentityListComponent } from './identity/list/identity-list.component';
import { ServicenumberThemeComponent } from './theme/servicenumber-theme.component';
import { ImageViewerComponent } from '../../avatar-modal/image-viewer/image-viewer.component';

@Component({
  selector: 'app-servicenumber-detail',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzSelectModule,
    NzCardModule,
    NzTabsModule,
    NzDividerModule,
    NzGridModule,
    NzIconModule,
    NzSpinModule,
    NzModalModule,
    NzSwitchModule,
    NzInputNumberModule,
    NzUploadModule,
    PersonalRobotSettingComponent,
    PersonalBroadcastListComponent,
    PersonalWebchatSettingComponent,
    PersonalInboundReasonComponent,
    NzBreadCrumbModule,
    ServiceMemberComponent,
    ServiceChannelComponent,
    ServiceNumberAuthComponent,
    NzToolTipModule,
    ServiceConfigComponent,
    ServicenumberThemeComponent,
    AvatarUploadModalComponent,
    NzAvatarModule,
    NzTagModule,
    IdentityListComponent,
    ImageViewerComponent,
    ChatExportComponent,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './servicenumber-detail.component.html',
  styleUrls: ['./servicenumber-detail.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ServicenumberDetailComponent implements OnInit {
  private readonly http = inject(HttpService);
  private readonly imageService = inject(ImageService); // 注入 ImageService
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly message = inject(NzMessageService);
  private readonly modal = inject(NzModalService);
  private readonly modalRef = inject(NzModalRef, { optional: true });
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly location = inject(Location);
  private readonly nzModalData = inject(NZ_MODAL_DATA, { optional: true }) || {};

  // 表单
  basicForm!: FormGroup;
  serviceNumberId: string = '';
  serviceNumberName: string = '';
  serviceNumberData: any;
  // 数据加载状态
  loading = false;
  submitting = false;
  tenantId: string = '';
  tenantType: string = '';
  currentLoginTenantType: string = '';
  isAdd = false;

  members: any[] = [];
  // 服务号类型选项
  typeOptions: any[] = [];

  // 公开类型选项
  openTypeOptions: any[] = [];

  // 状态选项
  statusOptions: any[] = [];

  // 满意度调查方式选项
  surveyTypeOptions: any[] = [];

  // 在类属性部分添加临时头像存储变量
  tempAvatarFile: any = null;
  avatarUrl: string = '';
  isAvatarModalVisible = false;
  isAvatarUploading = false;

  filteredTypeOptions: any[] = [];
  isOpenTypeDisabled: boolean = false;
  imageViewerVisible = false;
  selectedTabIndex: number = 0;
  isCompactViewport = false;
  mobileDesktopPromptDismissed = false;
  private requestedTab: string | null = null;
  private returnUrl: string = '';
  @ViewChild('embeddedWebchatSetting') embeddedWebchatSetting?: PersonalWebchatSettingComponent;
  ngOnInit(): void {
    this.initCurrentLoginTenantType();
    this.updateViewportState();
    this.initForms();
    this.initOptions();
    const scopedTenantId = this.route.parent?.snapshot.paramMap.get('tenantId') || this.route.snapshot.paramMap.get('tenantId') || '';

    if (this.isCreateModalMode) {
      this.serviceNumberId = '';
      this.isAdd = true;
      this.tenantType = this.nzModalData.tenantType ?? '';
      this.tenantId = this.nzModalData.tenantId ?? '';
      this.returnUrl = this.normalizeReturnUrl(this.nzModalData.returnUrl || '');
      this.basicForm.patchValue({ tenantId: this.tenantId });
      this.initFilteredTypeOptions();
      this.applyTabFromParam();
      return;
    }

    this.route.paramMap.subscribe(params => {
      this.serviceNumberId = params.get('id') ?? '';
      this.isAdd = this.serviceNumberId === '';
      this.route.queryParamMap.subscribe(queryParams => {
        this.tenantType = queryParams.get('tenantType') ?? '';
        this.tenantId = this.tenantId || scopedTenantId || queryParams.get('tenantId') || '';
        this.returnUrl = this.normalizeReturnUrl(queryParams.get('returnUrl') || '');
        this.requestedTab = queryParams.get('tab');
        if (this.isAdd) {
          // 新增模式，使用路径tenantId（回退query）
          this.basicForm.patchValue({ tenantId: this.tenantId });
        } else {
          // 编辑模式，加载服务号数据
          this.loadServiceNumberData();
        }
        this.initFilteredTypeOptions();
        this.applyTabFromParam();
      });
    });
  }

  get isCreateModalMode(): boolean {
    return !!this.nzModalData.createMode && !!this.modalRef;
  }

  get showMobileDesktopPrompt(): boolean {
    return this.isCompactViewport && !this.mobileDesktopPromptDismissed;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateViewportState();
  }

  dismissMobileDesktopPrompt(): void {
    this.mobileDesktopPromptDismissed = true;
    this.cdr.markForCheck();
  }

  // 修改初始化表单方法，添加avatarFile字段
  initForms(): void {
    // 基础信息表单
    this.basicForm = this.fb.group({
      id: [null],
      name: [null, [Validators.required]],
      type: ['Professional', [Validators.required]],
      description: [null, [Validators.required]], // 添加必填验证
      openType: ['External', [Validators.required]],
      status: [{ value: 'Enable', disabled: true }, [Validators.required]], // 设置状态为禁用
      avatarId: [null], // 添加必填验证
      avatarUrl: [null],
      avatarFile: [null], // 添加文件字段
      tenantId: [null, [Validators.required]]
    });
  }

  // 初始化选项数组
  initOptions(): void {
    // 服务号类型选项
    this.typeOptions = [
      { label: this.i18n.fanyi('servicenumber.type.official'), value: 'Official' },
      { label: this.i18n.fanyi('servicenumber.type.manage'), value: 'Manage' },
      { label: this.i18n.fanyi('servicenumber.type.professional'), value: 'Professional' },
      { label: this.i18n.fanyi('servicenumber.type.normal'), value: 'Normal' },
      { label: this.i18n.fanyi('servicenumber.type.boss'), value: 'Boss' }
    ];

    // 公开类型选项
    this.openTypeOptions = [
      { label: this.i18n.fanyi('servicenumber.open-type.internal'), value: 'Internal' },
      { label: this.i18n.fanyi('servicenumber.open-type.external'), value: 'External' }
    ];

    // 状态选项
    this.statusOptions = [
      { label: this.i18n.fanyi('servicenumber.status.new'), value: 'New' },
      { label: this.i18n.fanyi('servicenumber.status.enable'), value: 'Enable' },
      { label: this.i18n.fanyi('servicenumber.status.disable'), value: 'Disable' }
    ];

    // 满意度调查方式选项
    this.surveyTypeOptions = [
      { label: this.i18n.fanyi('servicenumber.survey-type.star'), value: 'Star' },
      { label: this.i18n.fanyi('servicenumber.survey-type.emoji'), value: 'Emoji' }
    ];
  }

  private updateViewportState(): void {
    if (typeof window === 'undefined') {
      return;
    }
    const nextState = window.innerWidth <= 767;
    if (nextState === this.isCompactViewport) {
      return;
    }
    this.isCompactViewport = nextState;
    if (!nextState) {
      this.mobileDesktopPromptDismissed = false;
    }
    this.cdr.markForCheck();
  }

  // 加载服务号数据
  loadServiceNumberData(): void {
    this.loading = true;
    this.cdr.detectChanges();
    this.http
      .post(`${ConstantUtil.ADMIN_SERVICENUMBER_DETAIL}/${this.serviceNumberId}`, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          // 兼容后端数据结构：可能是扁平结构，或嵌套在 serviceNumberModel 中
          const data = res?.data?.serviceNumberModel ?? res?.data ?? {};
          this.serviceNumberData = res.data;
          this.basicForm.patchValue(data || {});
          this.serviceNumberName = data?.name ?? '';
          this.tenantId = data?.tenantId ?? res?.data?.tenantModel?.id ?? '';
          if (data?.status === 'Enable') {
            this.basicForm.get('type')?.disable();
            this.basicForm.get('openType')?.disable();
          }
          if (data?.avatarId) {
            this.loadAvatarImage(data.avatarId);
          }
          if (this.tenantId) {
            this.loadTenantCapabilityConfig(this.tenantId);
          }
          this.applyTabFromParam();
          this.cdr.detectChanges();
        } else {
          this.message.error(this.i18n.fanyi('servicenumber.load-detail-failed'));
        }
      });
  }

  loadAvatarImage(avatarId: string): void {
    this.imageService
      .loadImage(avatarId, 'M')
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(imageUrl => {
        if (imageUrl) {
          this.basicForm.patchValue({ avatarUrl: imageUrl });
          this.avatarUrl = imageUrl;
          this.cdr.detectChanges();
        } else {
          console.error(this.i18n.fanyi('servicenumber.load-avatar-failed'));
          this.basicForm.patchValue({ avatarUrl: '' });
          this.cdr.detectChanges();
        }
      });
  }

  private loadTenantCapabilityConfig(tenantId: string): void {
    this.http.post(`${ConstantUtil.TENANT_CAPABILITY_V1_ITEM}/${tenantId}`, {}).subscribe(res => {
      if (!res?.success) {
        return;
      }
      const capabilityCodes = resolveCapabilityAppCodesFromApiCodes(res?.data?.capabilities ?? []);
      if (!capabilityCodes.length) {
        return;
      }
      this.serviceNumberData = {
        ...(this.serviceNumberData ?? {}),
        tenantModel: {
          ...(this.serviceNumberData?.tenantModel ?? {}),
          capabilityCodes
        }
      };
      // Capability fetch can insert the auth tab before webchat and shift tab indexes.
      // Re-apply the requested tab after capabilities arrive so refresh on `tab=webchat`
      // keeps the embedded content mounted on the correct tab.
      this.applyTabFromParam();
      this.cdr.detectChanges();
    });
  }

  // 显示头像上传对话框
  showAvatarUploadModal(): void {
    this.isAvatarModalVisible = true;
  }

  // 取消头像上传
  handleAvatarModalCancel(): void {
    this.isAvatarModalVisible = false;
  }

  // 确认头像上传
  handleAvatarCropped(event: { blob: Blob; width: number }): void {
    this.tempAvatarFile = new File([event.blob], 'avatar.png', { type: 'image/png' });

    if (this.isAdd) {
      // 创建本地预览URL
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.avatarUrl = e.target.result;
        this.basicForm.patchValue({
          avatarUrl: this.avatarUrl
        });
        this.handleAvatarModalCancel();
        this.cdr.detectChanges();
      };
      reader.readAsDataURL(this.tempAvatarFile);
    } else {
      // 发送请求
      this.http.postFormData(ConstantUtil.TENAT_SERVICENUMBER_AVATAR_SET, { id: this.serviceNumberId }, this.tempAvatarFile).subscribe({
        next: res => {
          if (res.success) {
            this.message.success(this.i18n.fanyi('servicenumber.avatar-upload-success'));
            this.loadServiceNumberData();
          } else {
            this.message.error(res.msg ?? this.i18n.fanyi('servicenumber.avatar-upload-failed'));
          }
          this.isAvatarUploading = false;
          this.handleAvatarModalCancel();
          this.cdr.detectChanges();
        },
        error: error => {
          console.error('上传头像出错:', error);
          this.message.error(this.i18n.fanyi('servicenumber.avatar-upload-failed'));
          this.isAvatarUploading = false;
          this.cdr.detectChanges();
        }
      });
    }
  }
  saveServiceNumber(): void {
    // 验证表单
    if (this.basicForm.invalid) {
      Object.values(this.basicForm.controls).forEach(control => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
      this.message.warning(this.i18n.fanyi('servicenumber.complete-basic-info'));
      return;
    }

    // 根据是否为新增模式调用不同的方法
    if (this.isAdd) {
      this.createServiceNumber();
    } else {
      this.updateServiceNumber();
    }
  }

  // 创建服务号的方法
  createServiceNumber(): void {
    if (!this.tempAvatarFile && !this.avatarUrl) {
      this.message.warning(this.i18n.fanyi('servicenumber.upload-avatar-required'));
      return;
    }
    const ownerMember = this.members.find(member => member.privilege === 'Owner');
    if (!ownerMember) {
      this.message.warning(this.i18n.fanyi('servicenumber.add-owner-required'));
      return;
    }
    this.submitting = true;
    const basicData = this.basicForm.value;

    basicData.ownerId = ownerMember.memberId;

    // 处理其他成员
    if (this.members && this.members.length > 0) {
      // 过滤掉 Owner，只保留其他成员
      const otherMembers = this.members
        .filter(member => member.privilege !== 'Owner')
        .map(member => ({
          memberId: member.memberId,
          privilege: member.privilege
        }));

      if (otherMembers.length > 0) {
        otherMembers.forEach((member, index) => {
          basicData[`members[${index}].memberId`] = member.memberId;
          basicData[`members[${index}].privilege`] = member.privilege;
        });
      }
    }
    this.http
      .postFormData(ConstantUtil.ADMIN_SERVICENUMBER_CREATE, basicData, this.tempAvatarFile)
      .pipe(
        finalize(() => {
          this.submitting = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          this.message.success(this.i18n.fanyi('servicenumber.create-success'));
          this.tempAvatarFile = null; // 清除临时文件
          if (this.isCreateModalMode) {
            this.modalRef?.close({ createdId: res.data.id });
          }
          this.navigateToDetailPage(res.data.id);
        } else {
          this.message.error(`${this.i18n.fanyi('servicenumber.create-failed')}: ${res.msg ?? ''}`);
        }
      });
  }

  // 更新服务号的方法
  updateServiceNumber(): void {
    this.submitting = true;
    const basicData = this.basicForm.value;

    this.http
      .post(ConstantUtil.ADMIN_SERVICENUMBER_UPDATE, basicData)
      .pipe(
        finalize(() => {
          this.submitting = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          this.message.success(this.i18n.fanyi('servicenumber.update-success'));
          this.tempAvatarFile = null; // 清除临时文件
          this.cdr.detectChanges();
        } else {
          this.message.error(`${this.i18n.fanyi('servicenumber.update-failed')}: ${res.msg ?? ''}`);
        }
      });
  }
  goBack(): void {
    if (this.isCreateModalMode) {
      this.modalRef?.close();
      return;
    }

    const navigateBack = () => {
      if (this.returnUrl) {
        this.router.navigateByUrl(this.returnUrl);
        return;
      }
      // 直接返回到租户详情的服务号列表标签页
      if (this.tenantId) {
        this.router.navigate(['/tenants/detail', this.tenantId], {
          queryParams: { tab: 'servicenumbers' }
        });
      } else {
        // 兜底：如果缺少租户ID，则回到租户列表
        this.router.navigateByUrl('/tenants');
      }
    };

    if (this.shouldConfirmLeaveWebchat()) {
      this.modal.confirm({
        nzTitle: this.i18n.fanyi('tenant.detail.confirm-leave'),
        nzContent: this.i18n.fanyi('tenant.detail.confirm-leave-content'),
        nzOkText: this.i18n.fanyi('common.confirm'),
        nzCancelText: this.i18n.fanyi('common.cancel'),
        nzOnOk: () => navigateBack()
      });
      return;
    }
    navigateBack();
  }

  // 限制条件：仅一般租户(Common)或官方租户(Official)，且服务号类型非 Official/Manage
  private isAllowedTenantType(): boolean {
    const type = this.tenantType || this.serviceNumberData?.tenantModel?.type;
    return type === 'Common' || type === 'Official';
  }

  private isNonOfficialNonManageSN(): boolean {
    const snType = this.basicForm?.get('type')?.value;
    return snType !== 'Official' && snType !== 'Manage';
  }

  // 是否显示启用/禁用按钮
  shouldShowEnableButton(): boolean {
    const statusNotEnabled = this.basicForm?.get('status')?.value !== 'Enable';
    return !this.isAdd && statusNotEnabled && this.isAllowedTenantType() && this.isNonOfficialNonManageSN();
  }

  shouldShowDisableButton(): boolean {
    const statusEnabled = this.basicForm?.get('status')?.value === 'Enable';
    return !this.isAdd && statusEnabled && this.isAllowedTenantType() && this.isNonOfficialNonManageSN();
  }

  // 启用服务号
  enableServiceNumber(): void {
    if (!this.serviceNumberId) return;
    const url = `${ConstantUtil.TENANT_SERVICENUMBER_V1_ENABLE}/${this.serviceNumberId}`;
    this.loading = true;
    this.http
      .post(url, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res?.success === true && (res.data === true || res.data === undefined)) {
          this.message.success(this.i18n.fanyi('servicenumber.enable-success'));
          this.loadServiceNumberData();
        } else {
          this.message.error(this.i18n.fanyi('tenant.employee.operation-failed'));
        }
      });
  }

  // 禁用服务号
  disableServiceNumber(): void {
    if (!this.serviceNumberId) return;
    const url = `${ConstantUtil.TENANT_SERVICENUMBER_V1_DISABLE}/${this.serviceNumberId}`;
    this.loading = true;
    this.http
      .post(url, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res?.success === true && (res.data === true || res.data === undefined)) {
          this.message.success(this.i18n.fanyi('servicenumber.disable-success'));
          this.loadServiceNumberData();
        } else {
          this.message.error(this.i18n.fanyi('tenant.employee.operation-failed'));
        }
      });
  }
  isExternal(): boolean {
    return this.basicForm?.get('openType')?.value === 'External';
  }
  isBoss(): boolean {
    return this.basicForm?.get('type')?.value === 'Boss';
  }
  getServiceNumberTypeColor(type: string): string {
    const colorMap: { [key: string]: string } = {
      Official: 'red',
      Manage: 'blue',
      Professional: 'purple',
      Normal: 'green',
      Boss: 'orange'
    };
    return colorMap[type] ?? 'default';
  }
  getServiceNumberTypeLabel(type: string): string {
    const typeMap: { [key: string]: string } = {
      Official: this.i18n.fanyi('servicenumber.type.official'),
      Manage: this.i18n.fanyi('servicenumber.type.manage'),
      Professional: this.i18n.fanyi('servicenumber.type.professional'),
      Normal: this.i18n.fanyi('servicenumber.type.normal'),
      Boss: this.i18n.fanyi('servicenumber.type.boss')
    };
    return typeMap[type] || type;
  }

  handleMembersChange(members: any[]): void {
    this.members = members;
  }

  private initFilteredTypeOptions(): void {
    if (this.isAdd) {
      // 新建时只显示商务号和专业服务号
      this.filteredTypeOptions = this.typeOptions.filter(option => option.value === 'Boss' || option.value === 'Professional');
    } else {
      // 编辑时显示所有选项
      this.filteredTypeOptions = [...this.typeOptions];
    }
  }

  // 处理类型变更
  onTypeChange(value: string): void {
    if (value === 'Boss') {
      // 如果选择商务号，则公开类型设为对外且禁用
      this.basicForm.get('openType')?.setValue('External');
      this.isOpenTypeDisabled = true;
    } else {
      // 其他类型可以自由选择公开类型
      this.isOpenTypeDisabled = false;
    }
  }

  // Tab 键映射，按可见性动态生成
  getTabKeys(): string[] {
    // 将主题设置放到最后，其他标签按现有逻辑排列
    const keys: string[] = ['basic', 'config', 'robot'];
    if (this.shouldShowBroadcastTab()) {
      keys.push('broadcast');
    }
    if (this.shouldShowInboundReasonTab()) {
      keys.push('inbound-reason');
    }
    if (this.isExternal()) {
      keys.push('channel');
      if (this.isBoss() && !!this.serviceNumberId) {
        keys.push('identity');
      }
      if (this.shouldShowAuthTab()) {
        keys.push('auth');
      }
      keys.push('webchat');
    }
    if (this.canManageChatExport()) {
      keys.push('chat-export');
    }
    if (this.shouldShowThemeTab()) {
      keys.push('theme');
    }
    return keys;
  }

  canManageChatExport(): boolean {
    if (this.isAdd || !this.serviceNumberId) {
      return false;
    }
    if (this.serviceNumberData?.canManageChatExport !== undefined) {
      return !!this.serviceNumberData.canManageChatExport;
    }
    if (this.serviceNumberData?.privilege !== undefined) {
      return this.serviceNumberData.privilege === 'Manager';
    }
    if (this.serviceNumberData?.isManager !== undefined) {
      return !!this.serviceNumberData.isManager;
    }
    return true;
  }

  shouldShowInboundReasonTab(): boolean {
    const tenantType = this.tenantType || this.serviceNumberData?.tenantModel?.type || this.currentLoginTenantType;
    return !this.isAdd && !!this.serviceNumberId && (tenantType === 'Common' || tenantType === 'Person');
  }

  shouldShowBroadcastTab(): boolean {
    const tenantType = this.tenantType || this.serviceNumberData?.tenantModel?.type || this.currentLoginTenantType;
    return !this.isAdd && !!this.serviceNumberId && (tenantType === 'Common' || tenantType === 'Person');
  }

  shouldShowThemeTab(): boolean {
    return false;
  }

  shouldShowAuthTab(): boolean {
    if (this.currentLoginTenantType !== 'Common') {
      return false;
    }
    const targetTenantCodes = this.readCapabilityCodes(this.serviceNumberData?.tenantModel);
    return targetTenantCodes.includes(TENANT_CAPABILITY_MANAGE_SERVICE_NUMBER_AUTHORIZATION);
  }

  // 判断当前激活标签
  isTabActive(key: string): boolean {
    const keys = this.getTabKeys();
    return keys[this.selectedTabIndex] === key;
  }

  // 选中标签变化时，同步 URL 的 tab 参数
  onTabChange(index: number): void {
    if (index === this.selectedTabIndex) return;
    const keys = this.getTabKeys();
    const currentKey = keys[this.selectedTabIndex] ?? 'basic';
    if (currentKey === 'webchat' && this.hasWebchatUnsavedChanges()) {
      this.modal.confirm({
        nzTitle: this.i18n.fanyi('tenant.detail.confirm-leave'),
        nzContent: this.i18n.fanyi('tenant.detail.confirm-leave-content'),
        nzOkText: this.i18n.fanyi('common.confirm'),
        nzCancelText: this.i18n.fanyi('common.cancel'),
        nzOnOk: () => this.commitTabChange(index)
      });
      return;
    }
    this.commitTabChange(index);
  }

  private commitTabChange(index: number): void {
    this.selectedTabIndex = index;
    const keys = this.getTabKeys();
    const tab = keys[index] ?? 'basic';
    const urlTree = this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge'
    });
    const url = this.router.serializeUrl(urlTree);
    this.location.go(url);
    this.cdr.detectChanges();
  }

  private hasWebchatUnsavedChanges(): boolean {
    return !!this.embeddedWebchatSetting?.hasUnsavedChanges();
  }

  private shouldConfirmLeaveWebchat(): boolean {
    const currentKey = this.getTabKeys()[this.selectedTabIndex] ?? 'basic';
    return currentKey === 'webchat' && this.hasWebchatUnsavedChanges();
  }

  private normalizeReturnUrl(url: string): string {
    const value = String(url || '').trim();
    if (!value) return '';
    if (!value.startsWith('/')) return '';
    if (value.startsWith('//')) return '';
    try {
      const tree = this.router.parseUrl(value);
      const primaryPath = tree.root.children['primary']?.segments.map(s => s.path).join('/') || '';
      if (
        !tree.queryParams['tab'] &&
        (primaryPath.startsWith('tenant/detail/') ||
          primaryPath.startsWith('tenants/detail/') ||
          primaryPath.startsWith('tenant/service-number-management/'))
      ) {
        tree.queryParams = {
          ...tree.queryParams,
          tab: 'servicenumbers'
        };
      }
      return this.router.serializeUrl(tree);
    } catch {
      return value;
    }
  }

  private initCurrentLoginTenantType(): void {
    try {
      const currentTenantRaw = localStorage.getItem('current_tenant');
      if (!currentTenantRaw) {
        this.currentLoginTenantType = '';
        return;
      }
      const currentTenant = JSON.parse(currentTenantRaw);
      this.currentLoginTenantType = currentTenant?.type ?? '';
    } catch {
      this.currentLoginTenantType = '';
    }
  }

  private readCurrentTenant(): any | null {
    try {
      const currentTenantRaw = localStorage.getItem('current_tenant');
      if (!currentTenantRaw) {
        return null;
      }
      return JSON.parse(currentTenantRaw);
    } catch {
      return null;
    }
  }

  private readCapabilityCodes(source: any): string[] {
    const codes = Array.isArray(source?.capabilityCodes) ? source.capabilityCodes : [];
    return codes.map((code: unknown) => String(code ?? '').trim()).filter((code: string) => !!code);
  }

  private navigateToDetailPage(serviceNumberId: string): void {
    const queryParams: Record<string, string> = {};
    if (this.tenantType) queryParams['tenantType'] = this.tenantType;
    if (this.returnUrl) queryParams['returnUrl'] = this.returnUrl;

    const tenantScopedMatch = this.returnUrl.match(/^\/tenant\/service-number-management\/([^/?#]+)/);
    if (tenantScopedMatch?.[1]) {
      this.router.navigate(['/tenant/service-number-management', tenantScopedMatch[1], 'detail', serviceNumberId], { queryParams });
      return;
    }

    const tenantDetailMatch = this.returnUrl.match(/^\/(?:tenant|tenants)\/detail\/([^/?#]+)/);
    const scopedTenantId = tenantDetailMatch?.[1] || this.tenantId;
    if (scopedTenantId) {
      this.router.navigate(['/tenant/service-number-management', scopedTenantId, 'detail', serviceNumberId], { queryParams });
      return;
    }

    if (this.tenantId) queryParams['tenantId'] = this.tenantId;
    this.router.navigate(['/servicenumber/detail', serviceNumberId], { queryParams });
  }

  // 根据路由参数应用选中的标签
  applyTabFromParam(): void {
    const keys = this.getTabKeys();
    const requested = this.requestedTab || this.route.snapshot.queryParamMap.get('tab');
    if (!requested) {
      this.selectedTabIndex = 0;
    } else {
      // 保留在當前頁簽，不做路由跳轉；渲染由模板條件控制
      const idx = keys.indexOf(requested);
      this.selectedTabIndex = idx >= 0 ? idx : 0;
    }
    this.cdr.detectChanges();
  }

  // 下拉等选项的 trackBy，减少重渲染
  trackByValue(index: number, item: any): any {
    return item?.value ?? item;
  }

  openImageViewer(imageUrl: string): void {
    this.imageViewerVisible = true;
  }
  closeImageViewer(): void {
    this.imageViewerVisible = false;
  }
}
